import type { InventoryMapBoundsCoords, InventoryMapLocation, InventoryMapSize } from './types.ts'
import type { InventoryMapObjectTypes } from './types.ts'
import { INVENTORY_MAP_NO_BIND_DEPENDENCY } from './constants.ts'

const MAP_BOUNDS_PADDING = 30
const MAP_TILE_SIZE = 256
const MAP_MIN_ZOOM = 0
const MAP_MAX_ZOOM = 21

/**
 * Дефолтные границы карты (Ленинградская обл.), которые используются для
 * первичной подгонки вида, когда нет предвыбранных объектов (пустой `ids`).
 */
export const DEFAULT_MAP_BOUNDS: InventoryMapBoundsCoords = {
  min_lon: 28.278579004013338,
  min_lat: 59.59481504696925,
  max_lon: 32.983795652156374,
  max_lat: 60.35546433909762,
}

/**
 * Возвращает фактический размер контейнера карты.
 */
export const getMapSize = (): InventoryMapSize => {
  const mapElement = document.querySelector<HTMLElement>('.w-invent-map__wrapper')

  return {
    width: mapElement?.clientWidth || window.innerWidth,
    height: mapElement?.clientHeight || window.innerHeight,
  }
}

/**
 * Дожидается, пока контейнер карты получит реальные размеры (ненулевые
 * clientWidth/clientHeight). На перезагрузке страницы карта монтируется после
 * прелоадера, и измерение до layout-а возвращает фолбэк на размер окна — из-за
 * чего расчётный zoom получается слишком крупным и крайние объекты обрезаются.
 *
 * @param maxFrames - предел ожидания в кадрах анимации (страховка от зависания)
 */
export const waitForMapContainerSize = async (maxFrames = 20): Promise<void> => {
  for (let i = 0; i < maxFrames; i++) {
    const el = document.querySelector<HTMLElement>('.w-invent-map__wrapper')
    if (el && el.clientWidth > 0 && el.clientHeight > 0) return

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  }
}

/**
 * Переводит широту в нормализованную Y-координату Web Mercator.
 */
const latToWorldY = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180)

  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)
}

/**
 * Переводит нормализованную Y-координату Web Mercator обратно в широту.
 */
const worldYToLat = (worldY: number) => {
  const mercator = Math.PI * (1 - 2 * worldY)

  return (Math.atan(Math.sinh(mercator)) * 180) / Math.PI
}

/**
 * Bounding box по массиву точек `[lng, lat]`.
 */
export function boundsFromLngLatPoints(points: number[][]): InventoryMapBoundsCoords | null {
  let min_lon = Infinity
  let max_lon = -Infinity
  let min_lat = Infinity
  let max_lat = -Infinity

  for (const point of points) {
    if (!Array.isArray(point) || point.length < 2) continue

    const lng = Number(point[0])
    const lat = Number(point[1])
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue

    min_lon = Math.min(min_lon, lng)
    max_lon = Math.max(max_lon, lng)
    min_lat = Math.min(min_lat, lat)
    max_lat = Math.max(max_lat, lat)
  }

  if (!Number.isFinite(min_lon)) return null

  return { min_lon, min_lat, max_lon, max_lat }
}

/**
 * Центр и zoom для объекта: точка — zoom 21, линия/полигон — fit по охвату геометрии.
 */
export function focusLocationFromCoordinates(
  coordinates: number[][],
): InventoryMapLocation | null {
  if (!coordinates?.length) return null

  if (coordinates.length === 1) {
    const [lng, lat] = coordinates[0]
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null

    return { center: [lng, lat], zoom: MAP_MAX_ZOOM }
  }

  const bounds = boundsFromLngLatPoints(coordinates)
  if (!bounds) return null

  return getBoundsFitLocation(bounds)
}

/**
 * Рассчитывает центр и масштаб карты по границам объектов.
 * По вертикали (широта) фит выполняется всегда; горизонтальный (долгота) фит
 * включается опцией `fitWidth` и нужен для набора объектов из поиска, чтобы
 * крайние по долготе элементы не уходили за пределы вьюпорта. Итоговый zoom —
 * минимальный из задействованных осей, чтобы bbox влезал целиком.
 *
 * @param coords - границы охвата объектов
 * @param options.fitWidth - дополнительно подгонять по горизонтали (по умолчанию false)
 */
export const getBoundsFitLocation = (
  coords: InventoryMapBoundsCoords,
  options?: { fitWidth?: boolean },
): InventoryMapLocation => {
  const { width, height } = getMapSize()
  const availableHeight = Math.max(height - MAP_BOUNDS_PADDING * 2, MAP_TILE_SIZE)
  const availableWidth = Math.max(width - MAP_BOUNDS_PADDING * 2, MAP_TILE_SIZE)

  const minWorldY = latToWorldY(coords.max_lat)
  const maxWorldY = latToWorldY(coords.min_lat)
  const worldHeight = Math.abs(maxWorldY - minWorldY)

  const zoomY = worldHeight
    ? Math.log2(availableHeight / (MAP_TILE_SIZE * worldHeight))
    : MAP_MAX_ZOOM

  let nextZoom = zoomY

  if (options?.fitWidth) {
    /* Mercator-X линейна по долготе, поэтому ширину считаем как долю от 360°. */
    const worldWidth = Math.abs(coords.max_lon - coords.min_lon) / 360
    const zoomX = worldWidth
      ? Math.log2(availableWidth / (MAP_TILE_SIZE * worldWidth))
      : MAP_MAX_ZOOM

    nextZoom = Math.min(zoomY, zoomX)
  }

  const centerWorldY = (minWorldY + maxWorldY) / 2

  return {
    center: [(coords.min_lon + coords.max_lon) / 2, worldYToLat(centerWorldY)],
    zoom: Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, nextZoom)),
  }
}

/**
 * Catalog tab ids allowed as map bind targets (`dependencies_list` wins over legacy `dependency`).
 */
export function resolveMapBindParentTypeIds(
  type: Pick<InventoryMapObjectTypes, 'dependencies_list' | 'dependency'>,
): number[] {
  const fromList = (type.dependencies_list ?? []).filter((id) => Number.isFinite(id) && id > 0)
  if (fromList.length > 0) return fromList
  if (type.dependency > 0 && type.dependency !== INVENTORY_MAP_NO_BIND_DEPENDENCY) {
    return [type.dependency]
  }
  return []
}

/** UNIVERSAL types may be created on the group when no bind target is nearby. */
export function canCreateMapObjectWithoutBindParent(functionalType: string | undefined): boolean {
  return functionalType === 'UNIVERSAL'
}
